import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@app/layouts/AppLayout';
import { AuthLayout } from '@app/layouts/AuthLayout';
import { ProtectedRoute } from '@app/router/ProtectedRoute';
import { PublicOnlyRoute } from '@app/router/PublicOnlyRoute';
import { DEFAULT_PATH, paths } from '@app/router/paths';
import { LoginPage } from '@features/auth/ui/LoginPage';
import { ForgotPasswordPage } from '@features/auth/ui/ForgotPasswordPage';
import { DashboardPage } from '@features/analytics/ui/DashboardPage';
import { UsersPage } from '@features/users/ui/UsersPage';
import { PlansPage } from '@features/plans/ui/PlansPage';
import { EngagementPage } from '@features/engagement/ui/EngagementPage';
import { NotFoundPage } from '@pages/NotFoundPage';

/**
 * Routes are grouped by their guard rather than listed flat, so a new page is
 * protected by where it is nested — you cannot forget to add a guard to it.
 *
 * Paths mirror the sidebar sections: `/budgeting/…` and `/accounting/…`, with
 * budgeting as the default landing area.
 */
export const router = createBrowserRouter([
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: paths.login, element: <LoginPage /> },
          { path: paths.forgotPassword, element: <ForgotPasswordPage /> },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Budgeting is the default section.
          { index: true, element: <Navigate to={DEFAULT_PATH} replace /> },

          {
            path: paths.budgeting.root,
            children: [
              { index: true, element: <Navigate to={paths.budgeting.dashboard} replace /> },
              { path: paths.budgeting.dashboard, element: <DashboardPage /> },
              { path: paths.budgeting.users, element: <UsersPage /> },
              { path: paths.budgeting.plans, element: <PlansPage /> },
              { path: paths.budgeting.engagement, element: <EngagementPage /> },
            ],
          },

          // Accounting is not built yet and cannot be entered. Anything under
          // it redirects rather than 404ing, so an old link lands somewhere
          // useful instead of on an error.
          { path: `${paths.accounting.root}/*`, element: <Navigate to={DEFAULT_PATH} replace /> },
          { path: paths.accounting.root, element: <Navigate to={DEFAULT_PATH} replace /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
